import React from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { PostHogProvider } from 'posthog-js/react';

import { isAnalyticsEnabled, isSessionRecordingDisabled } from '../analytics/env';

const APP_NAME = 'app';

const AnalyticsProvider = ({ children }) => {
  const apiKey = import.meta.env.VITE_POST_HOG_PROJECT_API_KEY;
  const apiHost = import.meta.env.VITE_POSTHOG_API_HOST;
  const uiHost = import.meta.env.VITE_POSTHOG_UI_HOST;
  const analyticsEnabled = isAnalyticsEnabled();
  const sessionRecordingDisabled = isSessionRecordingDisabled();

  if (!analyticsEnabled) {
    return children;
  }

  if (!apiKey || !apiHost) {
    // eslint-disable-next-line no-console
    console.warn(
      '[Analytics] enabled=true but apiKey/apiHost missing — analytics disabled.'
        + ' Check VITE_POST_HOG_PROJECT_API_KEY / VITE_POSTHOG_API_HOST.',
    );
    return children;
  }

  return (
    <PostHogProvider
      apiKey={apiKey}
      options={{
        api_host: apiHost,
        ui_host: uiHost,
        person_profiles: 'always',
        disable_session_recording: sessionRecordingDisabled,
        mask_all_element_attributes: false,
        mask_all_text: true,
        // Auto-pageview is unreliable here: PostHog's pushState hook doesn't
        // catch Ionic React Router navigations on this codebase, so SPA navs
        // silently miss most $pageview events. AnalyticsPageView (mounted
        // via PostHogPageViewShell in index.jsx) fires pageviews manually on
        // location.pathname change and synthesizes $pageview_id /
        // $prev_pageview_* so replay→event linkage and Paths-insight
        // breadcrumbs keep working.
        capture_pageview: false,
        session_recording: {
          maskAllInputs: true,
          blockSelector: '.MuiAvatar-root',
        },
        // Race: events captured between SDK init and the `loaded` callback
        // (which runs after /decide resolves) would miss the `app`
        // super-property if we only used `loaded.register`. `before_send`
        // tags every outbound event including the bootstrap ones;
        // `loaded.register` persists `app` to localStorage so future
        // sessions/tabs inherit it. Both required — removing either
        // regresses.
        before_send: (event) => {
          if (!event) return event;
          // Don't touch the session-recording stream. PostHog routes
          // $snapshot events through before_send, and reshaping the event
          // via spread breaks the snapshot uploader — recordings stop
          // landing. Snapshots are internal to recording, no `app` tag
          // needed.
          if (event.event === '$snapshot') return event;

          return { ...event, properties: { ...event.properties, app: APP_NAME } };
        },
        loaded: (ph) => ph.register({ app: APP_NAME }),
      }}
    >
      {children}
    </PostHogProvider>
  );
};

export default AnalyticsProvider;
